import { useMemo } from "react";
import { useLocation, useMatch, useNavigate, useParams } from "react-router-dom";
import { ORG_SEGMENT, PATHS } from "@constants/Paths";
import { useSubjectReportsContext } from "@stores/SubjectReportsContext";
export const latestReportId = (reports) => { var _a, _b, _c; return (_b = (_a = reports.find((report) => report.status === "completed")) === null || _a === void 0 ? void 0 : _a.reportId) !== null && _b !== void 0 ? _b : (_c = reports[0]) === null || _c === void 0 ? void 0 : _c.reportId; };
/**
 * The single resolver for report selection (Appendix A.4). Nothing else should decide this.
 *
 * The selection is community-wide and survives moving between a community's pages. Report and
 * Topics & Sources carry it in the path (`:assessmentId`); everywhere else the shell-level
 * provider remembers the last choice per community, so leaving a community's pages and coming
 * back lands on the report the user was looking at.
 *
 * The community page is the exception: it reads the community rather than a report — the score
 * trend, the sources, the recommendations — so it always shows the latest, whatever the user
 * picked on Report. That is why its date reads as a label and not as a choice.
 *
 * With no selection yet, the most recent completed report wins. Switching community reads a
 * different entry, so it falls back to that community's latest on its own.
 */
export const useSelectedReport = ({ communityId, reports }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { assessmentId } = useParams();
    const { getSelectedReportId, setSelectedReportId } = useSubjectReportsContext();
    const rememberedReportId = getSelectedReportId(communityId);
    // Both spellings of the same page: members reach it under `/o/:orgId`, staff on the bare path.
    const isCommunityPage = Boolean(useMatch(PATHS.COMMUNITY)) || Boolean(useMatch(`${ORG_SEGMENT}${PATHS.COMMUNITY}`));
    const selectedReportId = useMemo(() => {
        if (assessmentId)
            return assessmentId;
        // Resolved here rather than on the page, so the header's date and the widgets below it
        // cannot answer differently.
        if (isCommunityPage)
            return reports ? latestReportId(reports) : undefined;
        if (!reports)
            return rememberedReportId;
        const known = rememberedReportId && reports.some((report) => report.reportId === rememberedReportId);
        if (known)
            return rememberedReportId;
        return latestReportId(reports);
    }, [assessmentId, isCommunityPage, rememberedReportId, reports]);
    const selectReport = (reportId) => {
        // Remembered either way, so returning to the community page lands on the same report the
        // user was last looking at.
        if (communityId)
            setSelectedReportId(communityId, reportId);
        // Where the route owns the report, the path segment is the state — changing anything else
        // would leave the page rendering the old one. The hash is preserved so switching report on
        // Topics & Sources keeps the reader in the same category.
        if (assessmentId) {
            navigate(`${location.pathname.replace(assessmentId, reportId)}${location.hash}`, {
                state: location.state,
            });
        }
    };
    return { selectedReportId, selectReport };
};
